import { useEffect, useRef, useState, useCallback } from "react";
import { fabric } from "fabric";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Type, Square, Circle as CircleIcon, ImagePlus, Undo2, Redo2, Trash2, Save } from "lucide-react";
import api, { uploadFile } from "@/lib/api";
import { toast } from "sonner";

const PRESETS = [
  { id: "ig-post", label: "Instagram Post (1080×1350)", w: 1080, h: 1350 },
  { id: "ig-square", label: "Instagram Carré (1080×1080)", w: 1080, h: 1080 },
  { id: "story", label: "Story / Reel (1080×1920)", w: 1080, h: 1920 },
  { id: "linkedin", label: "LinkedIn (1200×627)", w: 1200, h: 627 },
];

export default function VisualEditor({ open, setOpen, publication, onSaved }) {
  const [containerEl, setContainerEl] = useState(null);
  const fabricRef = useRef(null);
  const fileRef = useRef(null);
  const [preset, setPreset] = useState(PRESETS[0]);
  const [bg, setBg] = useState("#F2EDE9");
  const [color, setColor] = useState("#1a1a1a");
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [, force] = useState(0);

  const SCALE = 0.42;

  const snapshot = useCallback(() => {
    const c = fabricRef.current;
    if (!c) return;
    undoStack.current.push(JSON.stringify(c.toJSON()));
    if (undoStack.current.length > 40) undoStack.current.shift();
    redoStack.current = [];
    force((x) => x + 1);
  }, []);

  useEffect(() => {
    if (!open || !containerEl) return;
    // Canvas créé hors de l'arbre React : fabric.js re-parente le <canvas>,
    // ce qui causait des erreurs "removeChild" à la fermeture de la modale.
    const el = document.createElement("canvas");
    containerEl.appendChild(el);
    const c = new fabric.Canvas(el, {
      width: Math.round(preset.w * SCALE),
      height: Math.round(preset.h * SCALE),
      backgroundColor: bg,
      preserveObjectStacking: true,
    });
    fabricRef.current = c;
    undoStack.current = [];
    redoStack.current = [];
    c.on("object:added", snapshot);
    c.on("object:modified", snapshot);
    c.on("object:removed", snapshot);
    return () => {
      c.dispose();
      fabricRef.current = null;
      containerEl.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preset, containerEl]);

  useEffect(() => {
    const c = fabricRef.current;
    if (c) { c.setBackgroundColor(bg, () => c.renderAll()); }
  }, [bg]);

  const addText = () => {
    const c = fabricRef.current;
    if (!c) return;
    const t = new fabric.Textbox("Votre texte", {
      left: 40, top: 40, width: c.width - 80, fontSize: 28, fill: color,
      fontFamily: "Satoshi, Inter, sans-serif", fontWeight: 700,
    });
    c.add(t); c.setActiveObject(t);
  };

  const addRect = () => {
    const c = fabricRef.current;
    if (!c) return;
    c.add(new fabric.Rect({ left: 60, top: 60, width: 140, height: 140, fill: color, rx: 8, ry: 8 }));
  };

  const addCircle = () => {
    const c = fabricRef.current;
    if (!c) return;
    c.add(new fabric.Circle({ left: 80, top: 80, radius: 70, fill: color }));
  };

  const addImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      fabric.Image.fromURL(reader.result, (img) => {
        const c = fabricRef.current;
        if (!c) return;
        const s = Math.min(c.width / img.width, c.height / img.height, 1) * 0.8;
        img.scale(s);
        c.add(img); c.centerObject(img);
      }, { crossOrigin: "anonymous" });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removeSelected = () => {
    const c = fabricRef.current;
    if (!c) return;
    c.getActiveObjects().forEach((o) => c.remove(o));
    c.discardActiveObject(); c.renderAll();
  };

  const undo = () => {
    const c = fabricRef.current;
    if (!c || undoStack.current.length <= 1) return;
    redoStack.current.push(undoStack.current.pop());
    const prev = undoStack.current[undoStack.current.length - 1];
    c.loadFromJSON(prev, () => c.renderAll());
  };

  const redo = () => {
    const c = fabricRef.current;
    if (!c || !redoStack.current.length) return;
    const next = redoStack.current.pop();
    undoStack.current.push(next);
    c.loadFromJSON(next, () => c.renderAll());
  };

  const save = async () => {
    const c = fabricRef.current;
    if (!c) return;
    c.discardActiveObject(); c.renderAll();
    const dataUrl = c.toDataURL({ format: "png", multiplier: 1 / SCALE });
    const blob = await (await fetch(dataUrl)).blob();
    try {
      const data = await uploadFile(new File([blob], `visuel-${publication.id}.png`, { type: "image/png" }));
      const url = data.url;
      await api.patch(`/publications/${publication.id}`, { visual_url: url });
      toast.success("Visuel enregistré sur la publication");
      setOpen(false);
      onSaved?.(url);
    } catch { toast.error("Échec de l'enregistrement"); }
  };

  const toolBtn = "flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-neutral-700 hover:bg-neutral-100 transition-colors text-left";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-5xl" data-testid="visual-editor" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading">Éditeur visuel — {publication?.title}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-5">
          <div className="flex-1 bg-neutral-100 rounded-xl p-4 flex items-start justify-center overflow-auto max-h-[70vh]">
            <div ref={setContainerEl} data-testid="editor-canvas" />
          </div>
          <div className="w-56 shrink-0 space-y-3">
            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">Format</p>
              <select data-testid="editor-preset" value={preset.id}
                onChange={(e) => setPreset(PRESETS.find((p) => p.id === e.target.id === e.target.value))}
                className="w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white"
              >
                {PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">Éléments</p>
              <button className={toolBtn} data-testid="editor-add-text" onClick={addText}><Type className="w-4 h-4" /> Texte</button>
              <button className={toolBtn} data-testid="editor-add-rect" onClick={addRect}><Square className="w-4 h-4" /> Rectangle</button>
              <button className={toolBtn} data-testid="editor-add-circle" onClick={addCircle}><CircleIcon className="w-4 h-4" /> Cercle</button>
              <button className={toolBtn} data-testid="editor-add-image" onClick={() => fileRef.current?.click()}><ImagePlus className="w-4 h-4" /> Image</button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={addImage} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">Fond</p>
                <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} data-testid="editor-bg"
                  className="w-full h-9 rounded-md cursor-pointer border border-neutral-200" />
              </div>
              <div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">Couleur</p>
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="editor-color"
                  className="w-full h-9 rounded-md cursor-pointer border border-neutral-200" />
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">Actions</p>
              <button className={toolBtn} data-testid="editor-undo" onClick={undo}><Undo2 className="w-4 h-4" /> Annuler</button>
              <button className={toolBtn} data-testid="editor-redo" onClick={redo}><Redo2 className="w-4 h-4" /> Rétablir</button>
              <button className={toolBtn} data-testid="editor-delete" onClick={removeSelected}><Trash2 className="w-4 h-4" /> Supprimer la sélection</button>
            </div>
            <Button data-testid="editor-save" onClick={save} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
              <Save className="w-4 h-4" /> Enregistrer le visuel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
